"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { io } from "socket.io-client";

/** Poll interval while the block is in view (see IntersectionObserver). */
const BASE_POLL_MS = 3_000;
const MAX_BACKOFF_MS = 60_000;
const SOCKET_SNAPSHOT_REFRESH_MS = 30_000;

/** Visible body rows (stable layout). */
const DISPLAY_ROWS = 10;
/** Max signatures retained across polls (merge / dedupe). */
const HISTORY_CAP = 40;

function normalizePoolAddress(raw) {
  const s = String(raw ?? "").trim();
  return s.length > 0 ? s : null;
}

function upsertAndTrimHistory(map, incoming) {
  for (const r of incoming) {
    const sig = r?.signature;
    if (sig && typeof sig === "string" && !sig.startsWith("__")) {
      map.set(sig, r);
    }
  }
  const sorted = [...map.values()].sort(
    (a, b) => (Number(b.timestamp) || 0) - (Number(a.timestamp) || 0),
  );
  const kept = sorted.slice(0, HISTORY_CAP);
  map.clear();
  for (const row of kept) {
    map.set(row.signature, row);
  }
}

function displayFromHistory(map) {
  const sorted = [...map.values()].sort(
    (a, b) => (Number(b.timestamp) || 0) - (Number(a.timestamp) || 0),
  );
  return sorted.slice(0, DISPLAY_ROWS);
}

function padActivityRows(rows) {
  const out = [...rows];
  let padIdx = 0;
  while (out.length < DISPLAY_ROWS) {
    out.push({
      signature: `__empty_${padIdx}`,
      _placeholder: true,
      timestamp: null,
      side: null,
      amount: null,
      quoteHint: null,
      trader: null,
      source: null,
    });
    padIdx += 1;
  }
  return out;
}

function relTime(tsSec) {
  if (tsSec == null || !Number.isFinite(tsSec)) return "—";
  const now = Date.now() / 1000;
  const d = Math.max(0, now - tsSec);
  if (d < 60) return `${Math.floor(d)}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

function shortenAddr(addr) {
  const s = String(addr || "").trim();
  if (s.length < 10) return s || "—";
  return `${s.slice(0, 4)}…${s.slice(-4)}`;
}

function shortenSig(sig) {
  const s = String(sig || "").trim();
  if (s.length < 14) return s || "—";
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

export function TokenDexLiveActivitiesTable({ mint, eagerLoad = false }) {
  const envSocketEnabled =
    String(process.env.NEXT_PUBLIC_POOL_ACTIVITY_SOCKET_ENABLED || "true").toLowerCase() !==
    "false";
  const [socketFailed, setSocketFailed] = useState(!envSocketEnabled);
  const useHttpPoll = !envSocketEnabled || socketFailed;
  const [isTabActive, setIsTabActive] = useState(
    typeof document === "undefined" ? true : !document.hidden,
  );
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    poolAddress: null,
    dexId: null,
    message: null,
    needsHeliusKey: false,
  });
  const [loading, setLoading] = useState(true);
  /** When false, polling and network are paused and a static loading shell is shown. */
  const [sectionInView, setSectionInView] = useState(eagerLoad);

  const mergedHistoryRef = useRef(new Map());
  const prevMintRef = useRef("");
  const lastPoolRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const nextDelayRef = useRef(BASE_POLL_MS);
  const sectionRootRef = useRef(null);
  const socketRef = useRef(null);
  const lastSocketPayloadAtRef = useRef(0);

  useEffect(() => {
    const mintTrim = mint?.trim() ?? "";
    if (prevMintRef.current === mintTrim) return;
    prevMintRef.current = mintTrim;
    mergedHistoryRef.current.clear();
    lastPoolRef.current = null;
    setRows([]);
    setLoading(true);
    setMeta({
      poolAddress: null,
      dexId: null,
      message: null,
      needsHeliusKey: false,
    });
    nextDelayRef.current = BASE_POLL_MS;
    fetchAbortRef.current?.abort();
    if (socketRef.current) {
      try {
        socketRef.current.emit("pool-activity:leave", mintTrim);
        socketRef.current.disconnect();
      } catch {
        // ignore
      }
      socketRef.current = null;
    }
  }, [mint]);

  useEffect(() => {
    if (eagerLoad) {
      setSectionInView(true);
      return undefined;
    }
    const el = sectionRootRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => setSectionInView(Boolean(entry?.isIntersecting)),
      { root: null, rootMargin: "240px 0px", threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [eagerLoad]);

  useEffect(() => {
    const onVisibilityChange = () => {
      setIsTabActive(!document.hidden);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const applyServerPayload = useCallback((json) => {
    if (Boolean(json?.needsHeliusKey)) {
      mergedHistoryRef.current.clear();
      lastPoolRef.current = null;
      setRows([]);
      setMeta({
        poolAddress: null,
        dexId: null,
        message: json.message ?? null,
        needsHeliusKey: true,
      });
      setLoading(false);
      return true;
    }

    const poolAddress = normalizePoolAddress(json?.poolAddress);
    const dexIdRaw = json?.dexId != null ? String(json.dexId).trim() : null;
    const dexId = dexIdRaw && dexIdRaw.length > 0 ? dexIdRaw : null;

    if (!poolAddress) {
      if (lastPoolRef.current && mergedHistoryRef.current.size > 0) {
        setMeta((m) => ({
          ...m,
          poolAddress: lastPoolRef.current,
          message: json?.message ?? m.message,
          needsHeliusKey: false,
        }));
        setRows(displayFromHistory(mergedHistoryRef.current));
        setLoading(false);
        return true;
      }
      mergedHistoryRef.current.clear();
      lastPoolRef.current = null;
      setRows([]);
      setMeta({
        poolAddress: null,
        dexId: null,
        message: json?.message ?? null,
        needsHeliusKey: false,
      });
      setLoading(false);
      return true;
    }

    setMeta({
      poolAddress,
      dexId,
      message: json?.message ?? null,
      needsHeliusKey: false,
    });

    if (lastPoolRef.current !== poolAddress) {
      mergedHistoryRef.current.clear();
      lastPoolRef.current = poolAddress;
    }

    const incoming = Array.isArray(json?.activities) ? json.activities : [];
    if (incoming.length > 0) {
      upsertAndTrimHistory(mergedHistoryRef.current, incoming);
    }
    setRows(displayFromHistory(mergedHistoryRef.current));
    setLoading(false);
    return true;
  }, []);

  const load = useCallback(async () => {
    const mintTrim = mint?.trim();
    if (!mintTrim) return;

    fetchAbortRef.current?.abort();
    const ac = new AbortController();
    fetchAbortRef.current = ac;

    try {
      const res = await fetch(
        `/api/token-dex/pool-activity?mint=${encodeURIComponent(mintTrim)}`,
        { cache: "no-store", signal: ac.signal },
      );
      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        const shouldBackoff = res.status === 429 || res.status >= 500;
        if (shouldBackoff) {
          nextDelayRef.current = Math.min(nextDelayRef.current * 2, MAX_BACKOFF_MS);
        }
        if (mergedHistoryRef.current.size === 0) {
          setLoading(true);
        } else {
          setLoading(false);
        }
        return;
      }
      nextDelayRef.current = BASE_POLL_MS;
      applyServerPayload(json);
    } catch (e) {
      if (e?.name === "AbortError") return;
      nextDelayRef.current = Math.min(nextDelayRef.current * 2, MAX_BACKOFF_MS);
      if (mergedHistoryRef.current.size === 0) {
        setLoading(true);
      } else {
        setLoading(false);
      }
    }
  }, [mint, applyServerPayload]);

  useEffect(() => {
    if (!useHttpPoll) return;
    if (!sectionInView) {
      fetchAbortRef.current?.abort();
      return;
    }

    nextDelayRef.current = BASE_POLL_MS;
    setLoading(true);
    let timerId;
    let stopped = false;

    const schedule = (delayMs) => {
      if (stopped) return;
      timerId = window.setTimeout(async () => {
        if (document.hidden) {
          schedule(nextDelayRef.current);
          return;
        }
        await load();
        schedule(nextDelayRef.current);
      }, delayMs);
    };

    load().finally(() => {
      schedule(nextDelayRef.current);
    });

    const onVisibilityChange = () => {
      if (document.hidden) return;
      window.clearTimeout(timerId);
      nextDelayRef.current = BASE_POLL_MS;
      load().finally(() => {
        schedule(nextDelayRef.current);
      });
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stopped = true;
      window.clearTimeout(timerId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      fetchAbortRef.current?.abort();
    };
  }, [sectionInView, load, useHttpPoll]);

  useEffect(() => {
    if (!envSocketEnabled || socketFailed) return;
    const mintTrim = mint?.trim();
    const shouldConnect = sectionInView && isTabActive && Boolean(mintTrim);

    if (!shouldConnect) {
      if (socketRef.current) {
        try {
          socketRef.current.emit("pool-activity:leave", mintTrim);
          socketRef.current.disconnect();
        } catch {
          // ignore
        }
        socketRef.current = null;
      }
      return;
    }

    let disposed = false;
    let snapshotTimerId = null;
    setLoading(true);

    const connect = async () => {
      await load();
      if (disposed) return;

      try {
        const boot = await fetch("/api/token-dex/pool-activity-socket", { cache: "no-store" });
        if (!boot.ok) {
          setSocketFailed(true);
          return;
        }
      } catch {
        setSocketFailed(true);
        return;
      }

      if (disposed) return;
      const socket = io({
        path: "/api/token-dex/socket",
        transports: ["websocket", "polling"],
        reconnection: false,
        timeout: 4000,
      });
      socketRef.current = socket;

      const failToHttpPoll = () => {
        if (disposed) return;
        try {
          socket.disconnect();
        } catch {
          // ignore
        }
        socketRef.current = null;
        setSocketFailed(true);
      };

      socket.on("connect_error", failToHttpPoll);
      socket.io?.on?.("error", failToHttpPoll);

      socket.on("connect", () => {
        socket.emit("pool-activity:join", mintTrim);
      });

      socket.on("pool-activity:update", (payload) => {
        if (payload?.mint && payload.mint !== mintTrim) return;
        lastSocketPayloadAtRef.current = Date.now();
        nextDelayRef.current = BASE_POLL_MS;
        applyServerPayload(payload || {});
      });

      snapshotTimerId = window.setInterval(() => {
        if (document.hidden) return;
        const ageMs = Date.now() - lastSocketPayloadAtRef.current;
        if (ageMs >= SOCKET_SNAPSHOT_REFRESH_MS) {
          load();
        }
      }, SOCKET_SNAPSHOT_REFRESH_MS);
    };

    connect();

    return () => {
      disposed = true;
      if (snapshotTimerId) window.clearInterval(snapshotTimerId);
      if (socketRef.current) {
        try {
          socketRef.current.emit("pool-activity:leave", mintTrim);
          socketRef.current.disconnect();
        } catch {
          // ignore
        }
        socketRef.current = null;
      }
    };
  }, [
    sectionInView,
    isTabActive,
    mint,
    envSocketEnabled,
    socketFailed,
    load,
    applyServerPayload,
  ]);

  const sub =
    meta.poolAddress && meta.dexId
      ? `Top pool (${meta.dexId}) · ${shortenAddr(meta.poolAddress)}`
      : meta.poolAddress
        ? `Pool · ${shortenAddr(meta.poolAddress)}`
        : "DEX swaps touching this token’s main pool";

  /** Off-screen: keep showing the last table (polling still pauses) so height does not collapse and scroll stays smooth. */
  const showLoadingPlaceholder = loading && rows.length === 0 && !meta.needsHeliusKey;

  return (
    <div
      ref={sectionRootRef}
      id="token-dex-live-activities"
      className="mt-6 w-full min-w-0 scroll-mt-24"
    >
      <div>
        <h3 className="text-base font-semibold text-white sm:text-lg">Live activities</h3>
        <p className="mt-1 text-xs text-gray-500 sm:text-sm">{sub}</p>
      </div>

      {/*
        iOS Safari: a lone overflow-x-auto can still widen document scrollWidth (page pans sideways).
        Outer overflow-x-hidden + overscroll-x-contain on the scrollport matches CommunitySignalNeighborGallery.
      */}
      <div className="mt-3 w-full min-w-0 max-w-full overflow-x-hidden">
        <div
          className="overflow-x-auto overscroll-x-contain rounded-xl border border-gray-700/80 bg-black/30 [-webkit-overflow-scrolling:touch]"
        >
        {showLoadingPlaceholder ? (
          <div className="flex min-h-[420px] flex-col items-center justify-center px-4 py-10">
            <p className="text-center text-sm text-gray-500">Loading activities</p>
          </div>
        ) : meta.needsHeliusKey ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500">{meta.message}</p>
        ) : !meta.poolAddress ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">
            {meta.message || "No DexScreener pool found for this mint yet."}
          </p>
        ) : (
          <>
            {rows.length === 0 ? (
              <p className="border-b border-gray-800 px-4 py-2.5 text-center text-xs text-gray-500">
                No recent swaps detected for this pool.
              </p>
            ) : null}
            <table className="w-full min-w-[640px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-gray-800 bg-gray-950/80 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2.5 sm:px-4">Time</th>
                  <th className="px-3 py-2.5 sm:px-4">Side</th>
                  <th className="px-3 py-2.5 sm:px-4">Amount</th>
                  <th className="hidden px-3 py-2.5 sm:table-cell sm:px-4 md:table-cell">Counter</th>
                  <th className="px-3 py-2.5 sm:px-4">Trader</th>
                  <th className="hidden px-3 py-2.5 lg:table-cell lg:px-4">Vendor</th>
                  <th className="px-3 py-2.5 text-right sm:px-4">Tx</th>
                </tr>
              </thead>
              <tbody className="text-gray-200">
                {padActivityRows(rows).map((r) =>
                  r._placeholder ? (
                    <tr
                      key={r.signature}
                      aria-hidden
                      className="border-b border-gray-800/80 last:border-0"
                    >
                      <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-gray-600 sm:px-4">
                        —
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 sm:px-4">—</td>
                      <td className="max-w-[140px] truncate px-3 py-2.5 tabular-nums text-gray-600 sm:max-w-none sm:px-4">
                        —
                      </td>
                      <td className="hidden px-3 py-2.5 text-gray-600 sm:table-cell sm:px-4 md:table-cell">
                        —
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-gray-600 sm:px-4">
                        —
                      </td>
                      <td className="hidden px-3 py-2.5 text-gray-600 lg:table-cell lg:px-4">—</td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right text-gray-600 sm:px-4">
                        —
                      </td>
                    </tr>
                  ) : (
                    <tr
                      key={r.signature}
                      className="border-b border-gray-800/80 last:border-0 hover:bg-gray-900/40"
                    >
                      <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-gray-400 sm:px-4">
                        {relTime(r.timestamp)}
                      </td>
                      <td className="px-3 py-2.5 sm:px-4">
                        <span
                          className={
                            r.side === "Buy"
                              ? "font-medium text-emerald-400"
                              : r.side === "Sell"
                                ? "font-medium text-rose-400"
                                : "text-gray-300"
                          }
                        >
                          {r.side}
                        </span>
                      </td>
                      <td className="max-w-[140px] truncate px-3 py-2.5 tabular-nums sm:max-w-none sm:px-4">
                        {r.amount ?? "—"}
                      </td>
                      <td className="hidden max-w-[160px] truncate px-3 py-2.5 text-gray-500 sm:table-cell sm:px-4 md:table-cell">
                        {r.quoteHint ?? "—"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-gray-400 sm:px-4">
                        {shortenAddr(r.trader)}
                      </td>
                      <td className="hidden max-w-[120px] truncate px-3 py-2.5 text-gray-500 lg:table-cell lg:px-4">
                        {r.source ?? "—"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right sm:px-4">
                        <a
                          href={`https://solscan.io/tx/${encodeURIComponent(r.signature)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-end gap-1 font-mono text-xs text-sky-400 hover:text-sky-300"
                        >
                          <span className="hidden sm:inline">{shortenSig(r.signature)}</span>
                          <span className="sm:hidden">Tx</span>
                          <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                        </a>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </>
        )}
        </div>
      </div>
      {meta.message && rows.length > 0 ? (
        <p className="mt-2 text-xs text-gray-600">{meta.message}</p>
      ) : null}
    </div>
  );
}
